import { ref } from 'vue'

import { API_URL } from '@/helpers/api-url.helper'
import type { BulkAnalysisJob } from '@/pages/nebula-ai/types/nebula-ai.types'

const STORAGE_KEY = 'nebula_ai_bulk_analysis_tracked_jobs'
const MAX_TRACKED_JOBS = 10
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000]

type BulkAnalysisEventType = 'snapshot' | 'status' | 'progress' | 'terminal'
type JobListener = (job: BulkAnalysisJob, eventType: BulkAnalysisEventType) => void

const trackedJobIds = ref<string[]>(readTrackedJobIds())
const listeners = new Set<JobListener>()
const controllers = new Map<string, AbortController>()
const reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>()
const reconnectAttempts = new Map<string, number>()
let isStarted = false

function readTrackedJobIds(): string[] {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')
    return Array.isArray(value)
      ? value
          .filter((jobId): jobId is string => typeof jobId === 'string' && !!jobId.trim())
          .slice(-MAX_TRACKED_JOBS)
      : []
  } catch {
    return []
  }
}

const persistTrackedJobIds = () => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trackedJobIds.value))
}

const isTerminal = (job: BulkAnalysisJob) => {
  const terminalJobs = ['completed', 'partial', 'failed', 'paused', 'paused_insufficient_credits']
  const terminalPhases = ['completed', 'partial', 'failed', 'skipped']

  return (
    terminalJobs.includes(job.status) &&
    terminalPhases.includes(job.insight_generation_status) &&
    terminalPhases.includes(job.index_status)
  )
}

const closeConnection = (jobId: string) => {
  controllers.get(jobId)?.abort()
  controllers.delete(jobId)
  const timer = reconnectTimers.get(jobId)
  if (timer) clearTimeout(timer)
  reconnectTimers.delete(jobId)
  reconnectAttempts.delete(jobId)
}

const untrack = (jobId: string) => {
  closeConnection(jobId)
  trackedJobIds.value = trackedJobIds.value.filter((id) => id !== jobId)
  persistTrackedJobIds()
}

const notify = (job: BulkAnalysisJob, eventType: BulkAnalysisEventType) => {
  listeners.forEach((listener) => listener(job, eventType))
}

const handleEvent = (job: BulkAnalysisJob, eventType: BulkAnalysisEventType) => {
  const isTerminalEvent = eventType === 'terminal' || isTerminal(job)
  notify(job, isTerminalEvent ? 'terminal' : eventType)
  if (isTerminalEvent) untrack(job.job_id)
}

const consumeStream = async (jobId: string) => {
  const controller = new AbortController()
  controllers.set(jobId, controller)

  try {
    const token = localStorage.getItem('tokenHub')
    if (!token) return

    const response = await fetch(
      `${API_URL}/ai/bulk-call-analysis/jobs/${encodeURIComponent(jobId)}/events`,
      {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: controller.signal,
      },
    )
    if (response.status === 404) {
      untrack(jobId)
      return
    }
    if (!response.ok || !response.body) throw new Error(`SSE request failed: ${response.status}`)

    reconnectAttempts.set(jobId, 0)
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let eventType: BulkAnalysisEventType = 'progress'
    let data = ''

    const dispatch = () => {
      if (!data) return
      try {
        const job = JSON.parse(data) as BulkAnalysisJob
        if (job.job_id === jobId) handleEvent(job, eventType)
      } catch {
        // A malformed SSE event must not take down the job tracker.
      } finally {
        eventType = 'progress'
        data = ''
      }
    }

    while (isStarted && !controller.signal.aborted) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
      const lines = buffer.split(/\r?\n/)
      buffer = lines.pop() || ''
      for (const line of lines) {
        if (!line) {
          dispatch()
        } else if (line.startsWith('event:')) {
          const candidate = line.slice(6).trim()
          if (['snapshot', 'status', 'progress', 'terminal'].includes(candidate)) {
            eventType = candidate as BulkAnalysisEventType
          }
        } else if (line.startsWith('data:')) {
          data += line.slice(5).trim()
        }
      }
      if (done) break
    }
    dispatch()
  } catch {
    if (controller.signal.aborted) return
  } finally {
    if (controllers.get(jobId) === controller) controllers.delete(jobId)
  }

  if (isStarted && trackedJobIds.value.includes(jobId)) scheduleReconnect(jobId)
}

const scheduleReconnect = (jobId: string) => {
  if (reconnectTimers.has(jobId)) return
  const attempt = reconnectAttempts.get(jobId) || 0
  const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]
  reconnectAttempts.set(jobId, attempt + 1)
  reconnectTimers.set(
    jobId,
    setTimeout(() => {
      reconnectTimers.delete(jobId)
      if (isStarted && trackedJobIds.value.includes(jobId)) void consumeStream(jobId)
    }, delay),
  )
}

const connect = (jobId: string) => {
  if (controllers.has(jobId) || reconnectTimers.has(jobId)) return
  void consumeStream(jobId)
}

const start = () => {
  if (isStarted) return
  isStarted = true
  trackedJobIds.value.forEach(connect)
}

const stop = () => {
  isStarted = false
  trackedJobIds.value.forEach(closeConnection)
}

const track = (jobId: string) => {
  if (!jobId) return
  const nextJobIds = [...trackedJobIds.value.filter((id) => id !== jobId), jobId].slice(
    -MAX_TRACKED_JOBS,
  )
  trackedJobIds.value.filter((id) => !nextJobIds.includes(id)).forEach(closeConnection)
  trackedJobIds.value = nextJobIds
  persistTrackedJobIds()
  if (isStarted) connect(jobId)
}

const subscribe = (listener: JobListener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const bulkAnalysisNotificationTracker = { start, stop, track, untrack, subscribe }
